import { useState } from 'react';
import { Link } from 'react-router-dom';
import '../../css/ContactPage.css';

export const ContactPage = () => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        companyName: '',
        email: '',
        phone: '',
        fleetSize: '',
        interest: '',
        message: ''
    });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.firstName || !formData.email) {
            setError('First name and email are required.');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setError('Please enter a valid email address.');
            return;
        }

        setError('');
        setLoading(true);

        try {
            const response = await fetch('https://gazfqccngynsvrw6yznopp5vpi0ehofa.lambda-url.us-west-1.on.aws/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: `${formData.firstName} ${formData.lastName}`,
                    email: formData.email,
                    subject: `Contact Form: ${formData.companyName || 'No company'} - ${formData.interest || 'General'}`,
                    message: `Phone: ${formData.phone || 'N/A'}\nFleet Size: ${formData.fleetSize || 'N/A'}\nInterest: ${formData.interest || 'N/A'}\n\n${formData.message}`
                })
            });

            if (!response.ok) throw new Error('Failed to send');

            setSuccess(true);
            setFormData({ firstName: '', lastName: '', companyName: '', email: '', phone: '', fleetSize: '', interest: '', message: '' });
        } catch {
            setError('Failed to send message. Please try again or call us directly.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="contact-page">
            {/* Hero */}
            <section className="contact-hero">
                <div className="contact-hero-container">
                    <span className="splash-section-label">★ GET IN TOUCH</span>
                    <h1>Talk to a <span className="underline-orange">real person</span> about your fleet.</h1>
                    <p>Questions about pricing, modules, or whether TotalEquipIQ fits your operation? Drop us a line. We'll get back to you within one business day, usually faster.</p>
                    <div className="contact-hero-phone">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" /></svg>
                        <span>Or Call: 432-561-3606</span>
                    </div>
                </div>
            </section>

            {/* Contact Form + Sidebar */}
            <section className="contact-form-section">
                <div className="contact-form-container">
                    <div className="contact-form-main">
                        <h2>Send us a message</h2>
                        <p>We'd love to talk about your operation and ask what you're trying to figure out. The more specific, the better the answer.</p>

                        {success ? (
                            <div className="contact-success">
                                <h3>Message sent!</h3>
                                <p>We'll get back to you within one business day. If you need something faster, give us a call at 432-561-3606.</p>
                            </div>
                        ) : (
                            <form className="contact-form" onSubmit={handleSubmit}>
                                <div className="contact-form-row">
                                    <div className="contact-form-field">
                                        <label>First Name *</label>
                                        <input
                                            type="text"
                                            value={formData.firstName}
                                            onChange={(e) => handleChange('firstName', e.target.value)}
                                            placeholder="First name"
                                        />
                                    </div>
                                    <div className="contact-form-field">
                                        <label>Company Name</label>
                                        <input
                                            type="text"
                                            value={formData.companyName}
                                            onChange={(e) => handleChange('companyName', e.target.value)}
                                            placeholder="e.g. Doe Earth Movers"
                                        />
                                    </div>
                                </div>

                                <div className="contact-form-row">
                                    <div className="contact-form-field">
                                        <label>Email *</label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => handleChange('email', e.target.value)}
                                            placeholder="your@email.com"
                                        />
                                    </div>
                                    <div className="contact-form-field">
                                        <label>Phone</label>
                                        <input
                                            type="tel"
                                            value={formData.phone}
                                            onChange={(e) => handleChange('phone', e.target.value)}
                                            placeholder="(555) 555-5555"
                                        />
                                    </div>
                                </div>

                                <div className="contact-form-row">
                                    <div className="contact-form-field">
                                        <label>What are you most interested in?</label>
                                        <select
                                            value={formData.interest}
                                            onChange={(e) => handleChange('interest', e.target.value)}
                                        >
                                            <option value="">Select an option</option>
                                            <option value="inspections">Inspections & QR</option>
                                            <option value="pm">Preventive Maintenance</option>
                                            <option value="tires">Tire Management</option>
                                            <option value="cost">Cost Tracking</option>
                                            <option value="training">Safety & Training</option>
                                            <option value="full">Full Platform (all modules)</option>
                                            <option value="demo">Just want a demo</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>
                                    <div className="contact-form-field">
                                        <label>Fleet Size</label>
                                        <select
                                            value={formData.fleetSize}
                                            onChange={(e) => handleChange('fleetSize', e.target.value)}
                                        >
                                            <option value="">Select fleet size</option>
                                            <option value="1-25">1–25 units</option>
                                            <option value="26-50">26–50 units</option>
                                            <option value="51-100">51–100 units</option>
                                            <option value="101-250">101–250 units</option>
                                            <option value="250+">250+ units</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="contact-form-field full-width">
                                    <label>Message</label>
                                    <textarea
                                        value={formData.message}
                                        onChange={(e) => handleChange('message', e.target.value)}
                                        placeholder="Tell us about your operation, what you're looking for, or any questions you have."
                                        rows={5}
                                    />
                                </div>

                                {error && <div className="contact-form-error">{error}</div>}

                                <button type="submit" className="splash-btn-primary contact-submit-btn" disabled={loading}>
                                    {loading ? 'Sending...' : 'Send Message'} →
                                </button>
                            </form>
                        )}
                    </div>

                    <div className="contact-form-sidebar">
                        <h3>Or reach us directly</h3>
                        <p>Here's a quick list of ways to get in touch without filling out a form.</p>

                        <div className="contact-sidebar-item">
                            <div className="contact-sidebar-icon">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" /></svg>
                            </div>
                            <div>
                                <strong>432-561-3606</strong>
                                <span>Monday–Friday, 8AM–5PM Central</span>
                            </div>
                        </div>

                        <div className="contact-sidebar-item">
                            <div className="contact-sidebar-icon">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><path d="M22 6l-10 7L2 6" /></svg>
                            </div>
                            <div>
                                <strong>hello@totalequipiq.com</strong>
                            </div>
                        </div>

                        <div className="contact-sidebar-item">
                            <div className="contact-sidebar-icon">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                            </div>
                            <div>
                                <strong>TotalEquipIQ</strong>
                                <span>PO Box 52121, Midland, TX 79710</span>
                            </div>
                        </div>

                        <div className="contact-sidebar-hours">
                            <h4>When you can reach us by phone</h4>
                            <div className="contact-hours-row">
                                <span>Mon–Friday</span>
                                <span>8 AM–5 PM CST</span>
                            </div>
                            <div className="contact-hours-row">
                                <span>Saturday</span>
                                <span>No appointment</span>
                            </div>
                            <div className="contact-hours-row">
                                <span>Sunday</span>
                                <span>No appointment</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Not Ready CTA */}
            <section className="contact-not-ready">
                <div className="contact-not-ready-container">
                    <h2>Not ready to talk yet?</h2>
                    <p>Sign up a free 30-day demo with your real equipment and your real crews. No credit card, no sales call required.</p>
                    <Link to="/register/free-demo" className="splash-btn-primary">Start Free Demo</Link>
                </div>
            </section>
        </div>
    );
};
